import { IRowNode } from "ag-grid-community";

import {
  AgProductType,
  CreateExperienceProductDto,
  DuplicateProductType,
  ExperienceProductDto,
  ExperienceVariationPricesDto,
  OfferProductDto,
  PriceChangeRounding,
  PriceDirection,
  TestProductInputStateType,
  VariationDto,
} from "@intelligems/ig-types";

import { SelectedProduct } from "../../store/types/productPicker";
import { VariationFe } from "../../types/schemas/variations";
import type { gqlProductVariant } from "../types/graphql";

import { fromGqlId } from "./shopify";

const priceRound = (
  key: PriceChangeRounding,
  value: number | undefined,
  price: number | null
) => {
  if (price === null) {
    return 0;
  }
  if (value !== undefined && value === 0) {
    return Math.round(price);
  }
  if (key === "custom" && value) {
    const roundDiff = 1 - value;
    return Math.round(price) - roundDiff;
  }
  if (key === "up") {
    return Math.ceil(price);
  }
  if (key === "down") {
    return Math.floor(price);
  }
  return price;
};

export const calculatePercent = (
  price: string | number | null,
  priceChange: string | number,
  direction?: PriceDirection,
  priceRoundingType?: PriceChangeRounding,
  priceCustomRoundingAmount?: number
): string => {
  let modifier = 1;

  if (direction && direction === "decrease") {
    modifier = -1;
  }
  if (typeof price === "string") {
    price = parseFloat(price.replace(new RegExp(",", "g"), ""));
  }

  if (typeof priceChange === "string") {
    priceChange =
      parseFloat(priceChange.replace(new RegExp(",", "g"), "")) / 100;
  }

  let newPercent: number | null = null;
  if (price) {
    newPercent = price * (1 + priceChange * modifier);
  }

  if (priceRoundingType && priceRoundingType !== "none") {
    const rounded = priceRound(
      priceRoundingType,
      priceCustomRoundingAmount,
      newPercent
    );
    return rounded.toFixed(2);
  } else return (newPercent || 0).toFixed(2);
};

export const calculateDollar = function (
  price: string | number,
  direction: PriceDirection,
  priceChange: string | number,
  priceRoundingType?: PriceChangeRounding,
  priceCustomRoundingAmount?: number,
  isCompare = false
): string {
  let modifier = 1;
  if (direction && direction === "decrease") {
    modifier = -1;
  }
  if (typeof price === "string") {
    price = parseFloat(price.replace(new RegExp(",", "g"), "."));
  }

  if (typeof priceChange === "string") {
    priceChange = parseFloat(priceChange.replace(new RegExp(",", "g"), "."));
  }

  if (!price && isCompare) {
    // leave this check
    if (typeof price === "number") {
      return price.toFixed(2);
    } else {
      return price;
    }
  }

  const newDollar = price + priceChange * modifier;

  if (priceRoundingType) {
    const rounded = priceRound(
      priceRoundingType,
      priceCustomRoundingAmount,
      newDollar
    );
    return rounded.toFixed(2);
  } else return newDollar.toFixed(2);
};

export const testProductsFromGqlId = (testProducts: ExperienceProductDto[]) => {
  return testProducts.map((product) => {
    return { ...product, id: fromGqlId(product.id) };
  });
};

export const getPriceFromTestProduct = function (
  variations: VariationFe[],
  variationPrice: ExperienceVariationPricesDto,
  valueToChange: TestProductInputStateType
) {
  const value = variationPrice[valueToChange];

  if (value !== undefined && value !== null) {
    if (typeof value === "number") {
      return value.toString();
    } else if (isNaN(Number(value.replace(new RegExp(",", "g"), "")))) {
      return null;
    } else if (Number(value) < 0) {
      return null;
    }
    return value;
  }

  const variation = variations.find((tg) => tg.name === variationPrice.name);

  if (variation) {
    if (variation.priceChange && variation.priceDirection) {
      if (variation.priceChangeUnit === "percent") {
        return calculatePercent(
          variationPrice[valueToChange]!,
          variation.priceChange!,
          variation.priceDirection,
          variation.priceRoundingType,
          variation.priceCustomRoundingAmount
        );
      } else {
        return calculateDollar(
          variationPrice[valueToChange]!,
          variation.priceDirection,
          variation.priceChange!,
          variation.priceRoundingType,
          variation.priceCustomRoundingAmount,
          valueToChange === "compareAtPrice"
        );
      }
    } else return variationPrice[valueToChange];
  }

  return null;
};

export const getPriceFromGql = function (
  group: VariationFe,
  variant: gqlProductVariant["variants"]["edges"][0],
  valueToChange: TestProductInputStateType
) {
  if (variant.node[valueToChange]) {
    if (group.priceChange && group.priceDirection) {
      if (group.priceChangeUnit === "percent") {
        return calculatePercent(
          variant.node[valueToChange]!,
          group.priceChange!,
          group.priceDirection,
          group.priceRoundingType,
          group.priceCustomRoundingAmount
        );
      } else {
        return calculateDollar(
          variant.node[valueToChange]!,
          group.priceDirection,
          group.priceChange!,
          group.priceRoundingType,
          group.priceCustomRoundingAmount
        );
      }
    } else return variant.node[valueToChange];
  }
  return null;
};

export const agProductToSelectedProduct = (
  agProducts: AgProductType[]
): SelectedProduct[] => {
  const productHash: { [key: string]: SelectedProduct } = {};

  for (const product of agProducts) {
    if (!(product.productId in productHash)) {
      productHash[product.productId] = {
        id: product.productId,
        title: product.productTitle,
        featuredImage: {
          url: product.productImage,
        },
      };
    }
  }
  return Object.values(productHash);
};

export const testProductsToInitialDuplicateAgProducts = (
  experienceProd: ExperienceProductDto[],
  variations: VariationDto[]
) => {
  const items: DuplicateProductType[] = [];
  for (const variation of variations) {
    if (!variation.isControl) {
      for (const product of experienceProd || []) {
        items.push({
          id: `${product.id}-${variation.id}`,
          productId: product.id,
          productTitle: product.title,
          productImage: product.image,
          eventStatus: "loading",
          handle: product.handle,
          variation: {
            id: variation.id,
            name: variation.name,
            isControl: false,
            shopifyStatus: undefined,
            syncStatus: "loading",
            issues: [],
            shopifyLink: "",
          },
        });
      }
    }
  }
  return items;
};

export const testProductToInitialDuplicateAgProducts = (
  expProducts: ExperienceProductDto,
  variations: VariationDto[]
): DuplicateProductType[] => {
  const items: DuplicateProductType[] = [];
  for (const variation of variations) {
    if (!variation.isControl) {
      items.push({
        id: `${expProducts.id}-${variation.id}`,
        productId: expProducts.id,
        productTitle: expProducts.title,
        productImage: expProducts.image,
        eventStatus: "loading",
        handle: expProducts.handle,
        variation: {
          id: variation.id,
          name: variation.name,
          isControl: false,
          shopifyStatus: undefined,
          syncStatus: "loading",
          issues: [],
          shopifyLink: "",
        },
      });
    }
  }
  return items;
};

export const testProductToAgProduct = (
  testProduct: CreateExperienceProductDto,
  variations: VariationFe[]
) => {
  const agProducts: AgProductType[] = [];

  testProduct.variants.forEach((variant) => {
    agProducts.push({
      productId: testProduct.id,
      productTitle: testProduct.title!,
      productImage: testProduct.image || null,
      handle: testProduct.handle,
      variantId: variant.id,
      variantTitle: variant.title,
      variantPrice: "",
      requiresSellingPlan: false,
      variationPrices: variant.variationPrices
        .map((group: ExperienceVariationPricesDto) => {
          const price = getPriceFromTestProduct(variations, group, "price");
          const compareAtPrice = getPriceFromTestProduct(
            variations,
            group,
            "compareAtPrice"
          );
          return {
            name: group.name,
            id: variant.id,
            variationId: group.variationId,
            price,
            ogPrice: group.price,
            compareAtPrice,
            ogComparePrice: group.compareAtPrice,
          };
        })
        .reduce((k, v) => ({ ...k, [v.name]: v }), {}),
    });
  });
  return agProducts;
};

interface AgProductHashById {
  [key: string]: CreateExperienceProductDto;
}

const agVariationStateToExpProductVariationState = (
  agVariationPrices: AgProductType["variationPrices"]
): ExperienceVariationPricesDto[] => {
  const variationPrices: ExperienceVariationPricesDto[] = [];
  for (const [key, value] of Object.entries(agVariationPrices)) {
    variationPrices.push({
      id: value.id,
      price: value.price!,
      variationId: value.variationId,
      compareAtPrice: value.compareAtPrice,
      name: value.name,
    });
  }
  return variationPrices;
};

export const AgNodesToExpProducts = (
  rows: IRowNode[]
): CreateExperienceProductDto[] => {
  const agProducts: AgProductType[] = [];

  for (const row of rows) {
    if (row) {
      agProducts.push(row as unknown as AgProductType);
    }
  }
  return agProductToExpProduct(agProducts);
};

export const agProductToExpProduct = (
  agProducts: AgProductType[]
): CreateExperienceProductDto[] => {
  const agProductHashById: AgProductHashById = {};

  for (const agProduct of agProducts) {
    if (agProduct.productId in agProductHashById) {
      agProductHashById[agProduct.productId].variants.push({
        id: fromGqlId(agProduct.variantId),
        title: agProduct.variantTitle,
        variationPrices: agVariationStateToExpProductVariationState(
          agProduct.variationPrices
        ),
      });
    } else {
      agProductHashById[agProduct.productId] = {
        id: fromGqlId(agProduct.productId),
        title: agProduct.productTitle,
        image: agProduct.productImage,
        handle: agProduct.handle,
        variants: [
          {
            id: fromGqlId(agProduct.variantId),
            title: agProduct.variantTitle,
            variationPrices: agVariationStateToExpProductVariationState(
              agProduct.variationPrices
            ),
          },
        ],
      };
    }
  }
  return Object.values(agProductHashById);
};

export const gqlProductVariantToAgProduct = (
  productVariant: gqlProductVariant,
  variations: VariationFe[]
): AgProductType[] => {
  const agProducts: AgProductType[] = [];
  productVariant.variants.edges.forEach((variant) => {
    agProducts.push({
      productId: productVariant.id,
      productTitle: productVariant.title,
      productImage: productVariant.featuredImage?.url || null,
      handle: "",
      variantId: variant.node.id,
      variantTitle: variant.node.title,
      variantPrice: variant.node.price,
      variationPrices: variations
        .map((group: VariationFe) => {
          const price =
            group.priceChange || group.isControl
              ? getPriceFromGql(group, variant, "price")
              : null;
          const compareAtPrice =
            group.priceChange || group.isControl
              ? getPriceFromGql(group, variant, "compareAtPrice")
              : null;
          return {
            name: group.name,
            id: variant.node.id,
            variationId: group.id,
            price,
            ogPrice: variant.node.price,
            compareAtPrice,
            ogCompareAtPrice: variant.node.compareAtPrice,
          };
        })
        .reduce((k, v) => ({ ...k, [v.name]: v }), {}),
    });
  });
  return agProducts;
};

export const gqlProductVariantToVolumeDiscountTestProduct = (
  productVariant: gqlProductVariant,
  variationId: string,
  offerId: string | null
): OfferProductDto => {
  return {
    variationId,
    offerId,
    id: fromGqlId(productVariant.id),
    title: productVariant.title,
    image: productVariant?.featuredImage?.url as string | null,
    inclusionType: "include",
    variants: productVariant.variants.edges.map((variant) => {
      return {
        id: fromGqlId(variant.node.id),
        title: variant.node.title,
        price: variant.node.price,
        compareAtPrice: variant.node.compareAtPrice,
      };
    }),
  };
};

export const offerTestProductToSelectedProduct = (
  productVariants: OfferProductDto[]
): SelectedProduct[] => {
  const productHash: { [key: string]: SelectedProduct } = {};

  for (const product of productVariants) {
    if (!(product.id in productHash)) {
      productHash[product.id] = {
        id: product.id,
        title: product.title,
        featuredImage: {
          url: product.image,
        },
      };
    }
  }
  return Object.values(productHash);
};

export const calculatePriceAutofill = (
  agProduct: AgProductType,
  variations: VariationFe[]
) => {
  let errors = 0;
  const controlGroup = variations.find((variation) => variation.isControl);
  let ogControlGroupPrice: string | null | undefined = null;
  let ogControlGroupComparePrice: string | null | undefined = null;

  for (const [key, value] of Object.entries(agProduct.variationPrices)) {
    if (key === controlGroup!.name) {
      ogControlGroupPrice = value.ogPrice;
      ogControlGroupComparePrice =
        value.ogCompareAtPrice &&
        parseFloat(value.ogCompareAtPrice) > 0 &&
        parseFloat(value.ogCompareAtPrice) > 0.0
          ? value.ogCompareAtPrice
          : undefined;
      break;
    }
  }

  for (const [key, value] of Object.entries(agProduct.variationPrices)) {
    const matchedVariation = variations.find((tg) => tg.name === key);
    if (
      controlGroup &&
      matchedVariation &&
      matchedVariation.priceChange &&
      ogControlGroupPrice
    ) {
      if (matchedVariation.priceChangeUnit === "percent") {
        value.price = calculatePercent(
          ogControlGroupPrice,
          matchedVariation.priceChange,
          matchedVariation.priceDirection,
          matchedVariation.priceRoundingType,
          matchedVariation.priceCustomRoundingAmount
        );

        if (ogControlGroupComparePrice) {
          value.compareAtPrice = calculatePercent(
            ogControlGroupComparePrice,
            matchedVariation.priceChange,
            matchedVariation.priceDirection,
            matchedVariation.priceRoundingType,
            matchedVariation.priceCustomRoundingAmount
          );
        }
      } else {
        value.price = calculateDollar(
          ogControlGroupPrice,
          matchedVariation.priceDirection,
          matchedVariation.priceChange
        );

        if (ogControlGroupComparePrice) {
          value.compareAtPrice = calculateDollar(
            ogControlGroupComparePrice,
            matchedVariation.priceDirection,
            matchedVariation.priceChange
          );
        }
      }
    }

    if (value.price && isNaN(parseFloat(value.price))) {
      value.price = "0";
    }
    if (value.compareAtPrice && isNaN(parseFloat(value.compareAtPrice))) {
      value.compareAtPrice = "0";
    }
    if (
      (value.price && Number(value.price) <= 0) ||
      (value.compareAtPrice && Number(value.compareAtPrice) < 0)
    ) {
      errors++;
    }
  }

  return { agProduct, errors };
};

export const calculatePriceDirection = function (
  priceChange: string | null | undefined | number
) {
  if (priceChange) {
    if (
      (typeof priceChange === "string"
        ? parseFloat(priceChange)
        : priceChange) > 0
    ) {
      return "increase";
    } else {
      return "decrease";
    }
  } else {
    return "decrease";
  }
};
